import { ComponentType, useEffect, useState } from 'react';

import { SchoolSurvey } from '@components/SchoolSurvey';
import { SurveySuccessDialog } from '@components/SurveySuccessDialog';
import { OptInConnection } from '@components/OptIn/components/OptInConnection';
import { OptInContactForm } from '@components/OptIn/components/OptInContactForm/OptInContactForm';
import { ShareInfo } from '@components/OptIn/components/ShareInfo';
import { GatherPersonalInfo } from '@components/GatherPersonalInfo';

import useOptInStore from '@store/OptInStore';
import useNotificationStore from '@store/NotificationStore';

import useAuthStore, { ProfileWithExtendedData } from '@store/AuthStore';
import useUserStore from '@store/UserStore';
import useUserPreferencesStore from '@store/UserPreferencesStore';
import useSchoolFavoritesStore from '@store/SchoolFavoritesStore';

import { isAlreadyGraduated } from '@utils/studentProgress';

import { SchoolSurveyData } from '@interfaces/favoriteSurvey';
import { InitialModalsStatus } from '@interfaces/optIn';

import { MIN_OPT_IN_USER_AGE, MIN_OPT_IN_USER_GRADE } from '@constants/optIn';

import { useNeedMoreUserInfo } from '@hooks/useNeedMoreUserInfo';

export interface WithSchoolSurveyModalProps {
  onOpenSurvey: (schoolData: SchoolSurveyData, isOpenFromNotes?: boolean) => void;
}

export type SchoolSurveyModalProps = Partial<WithSchoolSurveyModalProps>;

const initialModalsStatus: Partial<InitialModalsStatus> = {
  optInConnection: false,
  optInContactForm: false,
  gatherPersonalInfo: false,
  shareInfo: false,
  successDialog: false,
};

export const withSchoolSurveyModal =
  <T extends SchoolSurveyModalProps>(WrappedComponent: ComponentType<T>) =>
  (props: T & SchoolSurveyModalProps) => {
    const [schoolInfo, setSchoolInfo] = useState<SchoolSurveyData>({} as SchoolSurveyData);

    const { userData, userCard } = useAuthStore((state) => state.authSession);

    const [isOpenSurvey, setIsOpenSurvey] = useState(false);
    const [isOpenFromNotes, setIsOpenFromNotes] = useState(false);
    const [activeModalStatus, setActiveModalStatus] = useState(initialModalsStatus);
    const { isNotToBeAskedToShare } = useUserPreferencesStore((state) => state.userPreferences);

    const { share } = useOptInStore((state) => state.optInConnectionData);

    const notify = useNotificationStore((state) => state.notify);
    const setIsSurveyOpen = useSchoolFavoritesStore((state) => state.setIsSurveyOpen);
    const resetOptInConnectionData = useOptInStore((state) => state.resetOptInConnectionData);

    const {
      dateOfBirth,
      gradeLevel,
      graduationYear: reconciledGraduationYear,
    } = (userData?.profile ?? {}) as ProfileWithExtendedData;
    const graduationYear = userCard ? userCard.graduationYear : reconciledGraduationYear;
    const alreadyGraduated = isAlreadyGraduated();

    const isUserAllowedToShare = () => {
      if (!userData?.profile) {
        return false;
      }

      if (!dateOfBirth || (!graduationYear && !alreadyGraduated)) {
        return false;
      }

      const age = useUserStore.getState().getUserAge();

      return age >= MIN_OPT_IN_USER_AGE && ((gradeLevel || 0) >= MIN_OPT_IN_USER_GRADE || alreadyGraduated);
    };

    const handleOpenSurvey = (data: SchoolSurveyData, isOpenFromNotes: boolean) => {
      setIsSurveyOpen(!!data);
      setIsOpenSurvey(!!data);
      setSchoolInfo(data);
      setIsOpenFromNotes(isOpenFromNotes);
    };

    const handleCloseSurvey = () => {
      setIsOpenSurvey(false);
    };

    useEffect(() => {
      if (!isOpenSurvey) return undefined;

      return () => setIsSurveyOpen(false);
    }, [isOpenSurvey]);

    const setActiveModal = (field?: keyof InitialModalsStatus) => {
      if (field) {
        setActiveModalStatus({ ...initialModalsStatus, [field]: true });
      } else {
        setActiveModalStatus({ ...initialModalsStatus });
      }
    };

    const needMoreUserInformation = useNeedMoreUserInfo();

    useEffect(() => {
      if (activeModalStatus.optInConnection && needMoreUserInformation) {
        setActiveModal('gatherPersonalInfo');
        return;
      }

      if (
        (activeModalStatus.optInConnection || activeModalStatus.optInContactForm) &&
        (!isUserAllowedToShare() || isNotToBeAskedToShare)
      ) {
        setActiveModal('successDialog');
      }
    }, [
      needMoreUserInformation,
      isNotToBeAskedToShare,
      activeModalStatus.optInConnection,
      activeModalStatus.optInContactForm,
    ]);

    const handleCloseSuccessDialog = () => {
      if (share) {
        notify({
          title: `Your data now shared with ${schoolInfo.name}`,
          message: 'You can change permission all the time in the profile menu or on the detailed school page.',
          severity: 'success',
        });
      }

      setActiveModal();
      resetOptInConnectionData();
    };

    return (
      <>
        <WrappedComponent {...(props as T)} onOpenSurvey={handleOpenSurvey} />

        {isOpenSurvey && (
          <SchoolSurvey
            schoolInfo={schoolInfo}
            isOpenSurvey={isOpenSurvey}
            isOpenFromNotes={isOpenFromNotes}
            onCloseSurvey={handleCloseSurvey}
            setActiveModal={setActiveModal}
          />
        )}

        {activeModalStatus.gatherPersonalInfo && (
          <GatherPersonalInfo
            isOpenGatherPersonalInfo={activeModalStatus.gatherPersonalInfo}
            onSave={() => setActiveModal('optInConnection')}
            onCancel={() => setActiveModal('successDialog')}
            saveTitle="Save"
            cancelTitle="Skip for now"
          />
        )}

        {activeModalStatus.optInConnection && (
          <OptInConnection
            schoolInfo={schoolInfo}
            isOpenOptInConnection={activeModalStatus.optInConnection}
            setActiveModal={setActiveModal}
          />
        )}

        {activeModalStatus.shareInfo && (
          <ShareInfo
            isOpenShareInfo={activeModalStatus.shareInfo}
            onCloseShareInfo={setActiveModal}
            onOpenPreviousStep={() => setActiveModal('optInConnection')}
          />
        )}

        {activeModalStatus.optInContactForm && (
          <OptInContactForm
            isOpenOptInContactForm={activeModalStatus.optInContactForm}
            onGoBack={() => setActiveModal('optInConnection')}
            onGoNext={() => setActiveModal('successDialog')}
          />
        )}

        {activeModalStatus.successDialog && (
          <SurveySuccessDialog
            surveyType="school"
            additionalText={`Now, you granted permission to share your YouScience profile information with
            ${schoolInfo.name}.`}
            isOpenSuccessDialog={activeModalStatus.successDialog}
            onCloseSuccessDialog={handleCloseSuccessDialog}
          />
        )}
      </>
    );
  };
